import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';

export type MobileTask = {
  id: string;
  type: string;
  title: string;
  meta: string;
  urgent?: boolean;
  status: '待处理' | '已完成' | '已退回';
};

export type MobileMessage = {
  id: string;
  type: '预警' | '催办' | '审批' | '系统' | '通知';
  title: string;
  content: string;
  time: string;
  read: boolean;
  important?: boolean;
};

export type MobileCalendarEvent = {
  id: string;
  date: string;
  time: string;
  title: string;
  type: '待办' | '到期' | '值班' | '培训' | '会议';
};

type MobileBusinessState = {
  tasks: MobileTask[];
  messages: MobileMessage[];
  events: MobileCalendarEvent[];
  completedDuties: string[];
  signedResponsibility: boolean;
  confirmedRiskNotices: string[];
  confirmedInstitutionNotices: string[];
  rectifiedHazards: string[];
  attendedMeetings: string[];
  completeTask: (id: string) => void;
  returnTask: (id: string) => void;
  markMessageRead: (id: string) => void;
  markAllMessagesRead: () => void;
  completeDuty: (id: string) => void;
  signResponsibility: () => void;
  confirmRiskNotice: (id: string) => void;
  confirmInstitutionNotice: (id: string) => void;
  rectifyHazard: (id: string) => void;
  attendMeeting: (id: string) => void;
  addCalendarEvent: (event: MobileCalendarEvent) => void;
};

const STORAGE_KEY = 'safety-mobile-business-v2';

const seedTasks: MobileTask[] = [
  { id: 'T-001', type: '整改', title: 'K1523+200 边坡防护网破损整改', meta: '云州营运中心 · 剩余 8 小时', urgent: true, status: '待处理' },
  { id: 'T-002', type: '检查', title: '三季度防汛专项检查', meta: '集团安全监督管理部 · 剩余 22 小时', urgent: true, status: '待处理' },
  { id: 'T-003', type: '签署', title: '2026 年度安全目标责任书', meta: '集团安委会办公室 · 剩余 3 天', status: '待处理' },
  { id: 'T-004', type: '履职', title: '7 月重点风险监督检查记录', meta: '本月履职事项 · 剩余 5 天', status: '待处理' },
  { id: 'T-005', type: '学习', title: '安全生产费用管理办法宣贯', meta: '制度宣贯 · 剩余 7 天', status: '待处理' },
];

const seedMessages: MobileMessage[] = [
  { id: 'M-001', type: '预警', title: '安全员 C 证将于 28 天后到期', content: '请及时安排复训并提交续期材料。', time: '10:24', read: false, important: true },
  { id: 'M-002', type: '催办', title: '整改任务剩余 8 小时', content: 'K1523+200 边坡防护网破损整改即将到期。', time: '09:10', read: false, important: true },
  { id: 'M-003', type: '审批', title: '风险评估记录审核通过', content: 'HYGS-2026-FXD-0088 已完成评估审批。', time: '昨天', read: false },
  { id: 'M-004', type: '通知', title: '三季度安委会会议通知', content: '会议时间：7 月 22 日 09:00。', time: '昨天', read: false },
  { id: 'M-005', type: '系统', title: '弱网离线包更新完成', content: '已缓存 2 个检查表和 1 个风险点。', time: '07-18', read: true },
];

const seedEvents: MobileCalendarEvent[] = [
  { id: 'E-001', date: '2026-07-20', time: '09:30', title: '防汛专项检查', type: '待办' },
  { id: 'E-002', date: '2026-07-22', time: '09:00', title: '第三季度安全生产委员会会议', type: '会议' },
  { id: 'E-003', date: '2026-07-24', time: '全天', title: '集团本部应急值班', type: '值班' },
  { id: 'E-004', date: '2026-07-26', time: '14:00', title: '危化品运输安全培训', type: '培训' },
  { id: 'E-005', date: '2026-07-31', time: '18:00', title: '月度履职填报截止', type: '到期' },
];

type Persisted = Pick<MobileBusinessState, 'tasks' | 'messages' | 'events' | 'completedDuties' | 'signedResponsibility' | 'confirmedRiskNotices' | 'confirmedInstitutionNotices' | 'rectifiedHazards' | 'attendedMeetings'>;

function initialState(): Persisted {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as Persisted;
  } catch {
    // The prototype remains usable when storage is unavailable.
  }
  return {
    tasks: seedTasks,
    messages: seedMessages,
    events: seedEvents,
    completedDuties: [],
    signedResponsibility: false,
    confirmedRiskNotices: [],
    confirmedInstitutionNotices: [],
    rectifiedHazards: [],
    attendedMeetings: [],
  };
}

const MobileBusinessContext = createContext<MobileBusinessState | null>(null);

export function MobileBusinessProvider({ children }: PropsWithChildren) {
  const [initial] = useState(initialState);
  const [tasks, setTasks] = useState(initial.tasks);
  const [messages, setMessages] = useState(initial.messages);
  const [events, setEvents] = useState(initial.events);
  const [completedDuties, setCompletedDuties] = useState(initial.completedDuties);
  const [signedResponsibility, setSignedResponsibility] = useState(initial.signedResponsibility);
  const [confirmedRiskNotices, setConfirmedRiskNotices] = useState(initial.confirmedRiskNotices);
  const [confirmedInstitutionNotices, setConfirmedInstitutionNotices] = useState(initial.confirmedInstitutionNotices);
  const [rectifiedHazards, setRectifiedHazards] = useState(initial.rectifiedHazards);
  const [attendedMeetings, setAttendedMeetings] = useState(initial.attendedMeetings);

  useEffect(() => {
    const state: Persisted = { tasks, messages, events, completedDuties, signedResponsibility, confirmedRiskNotices, confirmedInstitutionNotices, rectifiedHazards, attendedMeetings };
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* noop */ }
  }, [tasks, messages, events, completedDuties, signedResponsibility, confirmedRiskNotices, confirmedInstitutionNotices, rectifiedHazards, attendedMeetings]);

  const value = useMemo<MobileBusinessState>(() => ({
    tasks,
    messages,
    events,
    completedDuties,
    signedResponsibility,
    confirmedRiskNotices,
    confirmedInstitutionNotices,
    rectifiedHazards,
    attendedMeetings,
    completeTask: (id) => setTasks((items) => items.map((item) => item.id === id ? { ...item, status: '已完成' } : item)),
    returnTask: (id) => setTasks((items) => items.map((item) => item.id === id ? { ...item, status: '已退回' } : item)),
    markMessageRead: (id) => setMessages((items) => items.map((item) => item.id === id ? { ...item, read: true } : item)),
    markAllMessagesRead: () => setMessages((items) => items.map((item) => ({ ...item, read: true }))),
    completeDuty: (id) => setCompletedDuties((items) => items.includes(id) ? items : [...items, id]),
    signResponsibility: () => setSignedResponsibility(true),
    confirmRiskNotice: (id) => setConfirmedRiskNotices((items) => items.includes(id) ? items : [...items, id]),
    confirmInstitutionNotice: (id) => setConfirmedInstitutionNotices((items) => items.includes(id) ? items : [...items, id]),
    rectifyHazard: (id) => setRectifiedHazards((items) => items.includes(id) ? items : [...items, id]),
    attendMeeting: (id) => setAttendedMeetings((items) => items.includes(id) ? items : [...items, id]),
    addCalendarEvent: (event) => setEvents((items) => items.some((item) => item.id === event.id) ? items : [...items, event]),
  }), [tasks, messages, events, completedDuties, signedResponsibility, confirmedRiskNotices, confirmedInstitutionNotices, rectifiedHazards, attendedMeetings]);

  return <MobileBusinessContext.Provider value={value}>{children}</MobileBusinessContext.Provider>;
}

export function useMobileBusiness() {
  const value = useContext(MobileBusinessContext);
  if (!value) throw new Error('useMobileBusiness 必须在 MobileBusinessProvider 内使用');
  return value;
}
